import { Component, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DxFormComponent } from 'devextreme-angular';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { ToastrService } from 'ngx-toastr';
import { map, filter, tap, switchMap, of } from 'rxjs';
import { ConfirmationDialogData } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { ExpandableTextEditorComponent } from 'src/app/components/shared/expandable-text-editor/expandable-text-editor.component';
import { PageStateFlags } from 'src/app/constants/page-state.constants';
import { TextTemplateCreationAndUpdateModel } from 'src/app/models/text-templates/text-template-creation-and-update.model';
import { TextTemplateTypeModel } from 'src/app/models/text-templates/text-template-type.model';
import { TextTemplateModel } from 'src/app/models/text-templates/text-template.model';
import { LoadingService } from 'src/app/services/loading.service';
import { PageStateService } from 'src/app/services/page-state.service';
import { TextTemplateService } from 'src/app/services/text-template.service';

@Component({
  selector: 'app-text-template-details',
  standalone: false,
  templateUrl: './text-template-details.component.html',
  styleUrl: './text-template-details.component.scss',
})
export class TextTemplateDetailsComponent {
  showDeleteTemplateModal: boolean = false;
  modalDeleteTemplateConfirmationOptions: ConfirmationDialogData | null = null;

  editingMode = false;
  currentTextTemplate: TextTemplateModel | null = null;

  generalFormData: TextTemplateCreationAndUpdateModel = {
    name: '',
    content: '',
    type: null
  };

  defaultSelectOptions = {
    showClearButton: true,
    stylingMode: 'outlined',
    displayExpr: 'name',
    valueExpr: 'id',
  } as DxSelectBoxTypes.Properties;
  
  templateTypes: DxSelectBoxTypes.Properties = {
    ...this.defaultSelectOptions,
    dataSource: [
      {
        id: TextTemplateTypeModel.ExtendedDescription,
        name: this.translateService.instant(
          'COMMON.FIELDS.TEXT-TEMPLATE-TYPES.EXTENDED-DESCRIPTION'
        ),
      },
      {
        id: TextTemplateTypeModel.AddParticipants,
        name: this.translateService.instant(
          'COMMON.FIELDS.TEXT-TEMPLATE-TYPES.ADD-PARTICIPANTS'
        ),
      },
      {
        id: TextTemplateTypeModel.InfoToParticipants,
        name: this.translateService.instant(
          'COMMON.FIELDS.TEXT-TEMPLATE-TYPES.INFO-TO-PARTICIPANTS'
        ),
      },
      {
        id: TextTemplateTypeModel.CancelMeeting,
        name: this.translateService.instant(
          'COMMON.FIELDS.TEXT-TEMPLATE-TYPES.CANCEL-MEETING'
        ),
      },
      {
        id: TextTemplateTypeModel.ChangeMeeting,
        name: this.translateService.instant(
          'COMMON.FIELDS.TEXT-TEMPLATE-TYPES.CHANGE-MEETING'
        ),
      },
    ],
    placeholder: '',
  };

  @ViewChild('generalForm', { static: false }) 
  generalForm!: DxFormComponent;

  @ViewChild('contentEditor')
  contentEditor!: ExpandableTextEditorComponent;

  constructor(
    private textTemplateService: TextTemplateService,
    private loadingService: LoadingService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private pageStateService: PageStateService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(
        map((params) => params['id']),
        filter((id) => !!id),
        tap((id) => {
          this.editingMode = id !== 'new';
        }),
        switchMap((id) => {
          return this.loadingService.load(
            this.editingMode
              ? this.textTemplateService.getTextTemplate(id)
              : of(null)
          );
        })
      )
      .subscribe((textTemplate) => {
        this.currentTextTemplate = textTemplate;

        if (textTemplate) {
          this.generalFormData = {
            name: textTemplate.name,
            content: textTemplate.content,
            type: textTemplate.type,
          };
        }
      });
  }

  submit() {
    const formValid =
      this.generalForm.instance.validate().isValid;

    const contentValid = this.contentEditor.validate();

    if (!formValid || !contentValid) {
      return;
    }

    const model = this.generalFormData;

    if (this.editingMode && this.currentTextTemplate) {
      const obs = this.textTemplateService.updateTextTemplate(
        this.currentTextTemplate.id,
        model
      );
      this.loadingService.load(obs).subscribe(() => {
        this.toastrService.success(
          this.translateService.instant(
            'TEXT-TEMPLATES.TEXT-TEMPLATE-SAVED'
          )
        );

        this.navigateBack();
      });
    } else {
      const obs = this.textTemplateService.addTextTemplate(model);
      this.loadingService.load(obs).subscribe(() => {
        this.toastrService.success(
          this.translateService.instant(
            'TEXT-TEMPLATES.TEXT-TEMPLATE-SAVED'
          )
        );
        
        this.navigateBack();
      });
    }
  }

  navigateBack(): void {
    this.pageStateService.setFlag(
      PageStateFlags.RestoreTextTemplatesOverview
    );

    this.router.navigate(['administration'], {
      queryParams: { tab: 'text-templates' },
    });
  }

  openDeleteTextTemplateConfirmationModal() {
    this.modalDeleteTemplateConfirmationOptions = {
      hideUndo: true,
      submitButtonText: this.translateService.instant('COMMON.DELETE'),
      message: this.translateService.instant(
        'ADMINISTRATION.DELETE-TEXT-TEMPLATE-CONFIRMATION-DIALOG.TEXT'
      ),
      dismissButtonText: this.translateService.instant('COMMON.CANCEL'),
    };
    this.showDeleteTemplateModal = true;
  }

  deleteTextTemplate() {
    if(!this.currentTextTemplate) return;

    this.loadingService
      .load(
        this.textTemplateService.deleteTextTemplate(this.currentTextTemplate.id)
      )
      .subscribe({
        next: () => {
          this.navigateBack();
        },
        error: () => {
          this.toastrService.error(
            this.translateService.instant(
              'COMMON.AN-ERROR-HAS-OCCURRED'
            )
          );
        },
      });
  }
}
